/* eslint-disable no-console */
import Alpine from 'alpinejs';
import { throttle } from 'lodash-es';

import '../utils/lazyloading';

// Helper to wait for d365mktformcapture to be available
function waitForD365FormCapture(timeout = 10000) {
  return new Promise((resolve, reject) => {
    if (window.d365mktformcapture) {
      resolve(window.d365mktformcapture);
      return;
    }

    const interval = 100;
    let elapsed = 0;

    const check = setInterval(() => {
      if (window.d365mktformcapture) {
        clearInterval(check);
        resolve(window.d365mktformcapture);
      } else if (elapsed >= timeout) {
        clearInterval(check);
        reject(new Error('d365mktformcapture failed to load'));
      }
      elapsed += interval;
    }, interval);
  });
}

// Header scrolled class
document.addEventListener(
  'scroll',
  throttle(() => {
    const header = document.querySelector('.header');
    header.classList.toggle('scrolled', window.scrollY > 0);
  }, 250)
);

// Bouncy element animations
document.addEventListener('DOMContentLoaded', () => {
  const items = document.querySelectorAll('.bouncy');

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animation-loaded');
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.25 }
  );

  items.forEach((item) => {
    observer.observe(item);
  });
});

// Contact form
Alpine.data('contact', () => ({
  isOpen: false,
  isSubmitted: false,
  showSuccessPopup: false,
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: '',
  platform: '',
  system: '',
  errorMessage: '',
  emptyFields: {
    firstName: false,
    lastName: false,
    email: false,
    message: false,
  },

  init() {
    if (globalThis.location.hash === '#contact') {
      this.isOpen = true;
    }

    document.addEventListener('open-contact', () => {
      this.isOpen = true;
    });

    if (globalThis.location.href.includes('contact_posted=true')) {
      this.isSubmitted = true;
      this.showSuccessPopup = true;
    }
  },

  submit($event) {
    $event.preventDefault();
    const $form = document.querySelector('.contact__inner-container-form');

    this.errorMessage = '';

    Object.keys(this.emptyFields).forEach((key) => {
      this.emptyFields[key] = !this[key];
    });

    if (!this.isFormComplete()) {
      this.errorMessage = 'Please complete the required fields.';
      return;
    }

    waitForD365FormCapture()
      .then((formCapture) => formCapture.waitForElement('#contact_form'))
      .then((form) => {
        const mappings = [
          {
            FormFieldName: 'contact[first_name]',
            DataverseFieldName: 'firstname',
          },
          {
            FormFieldName: 'contact[last_name]',
            DataverseFieldName: 'lastname',
          },
          {
            FormFieldName: 'contact[phone]',
            DataverseFieldName: 'mobilephone',
          },
          {
            FormFieldName: 'contact[email]',
            DataverseFieldName: 'emailaddress1',
          },
          {
            FormFieldName: 'contact[body]',
            DataverseFieldName: 'fus_contactusrequestdetails',
          },
          {
            FormFieldName: 'contact[platform]',
            DataverseFieldName: 'fus_currentecommerce',
            DataverseFieldValue: [
              { FormValue: 'Shopify', DataverseValue: '498400000' }, // Shopify
              { FormValue: ' BigCommerce', DataverseValue: '498400001' }, // BigCommerce
              { FormValue: ' Magento', DataverseValue: '498400002' }, // Magento
              { FormValue: ' Salesforce', DataverseValue: '498400003' }, // Salesforce
              { FormValue: ' WooCommerce', DataverseValue: '498400004' }, // WooCommerce
              { FormValue: ' Other', DataverseValue: '498400005' }, // Other
            ],
          },
          {
            FormFieldName: 'contact[system]',
            DataverseFieldName: 'fus_currentposerp',
            DataverseFieldValue: [
              { FormValue: 'NetSuite', DataverseValue: '498400000' }, // Netsuite
              { FormValue: ' Accumatica/MYOB', DataverseValue: '498400001' }, // Acumatica/MYOB
              { FormValue: ' SAP', DataverseValue: '498400002' }, // SAP
              { FormValue: ' Cin7', DataverseValue: '498400003' }, // Cin7
              { FormValue: ' Vend/Lightspeed', DataverseValue: '498400004' }, // Vend/Lightspeed
              { FormValue: ' Other', DataverseValue: '498400005' }, // Other
            ],
          },
          {
            FormFieldName: 'form_request_name',
            DataverseFieldName: 'fus_formrequestname',
          },
          {
            FormFieldName: 'lead_source_code',
            DataverseFieldName: 'leadsourcecode',
            DataverseFieldValue: [
              { FormValue: 'Marketing Created', DataverseValue: '4' },
            ],
          },
          {
            FormFieldName: 'product_family',
            DataverseFieldName: 'fus_productfamilytext',
          },
          {
            FormFieldName: 'marketing_source',
            DataverseFieldName: 'fus_marketingsource',
            DataverseFieldValue: [
              { FormValue: 'Digital - Contact', DataverseValue: '498400002' }, // Digital - Contact
            ],
          },
          {
            FormFieldName: 'pillar_text',
            DataverseFieldName: 'fus_fusion5pillar',
          },
          {
            FormFieldName: 'microsoft_form_type',
            DataverseFieldName: 'fus_formtype',
            DataverseFieldValue: [
              { FormValue: 'Contact', DataverseValue: '498400000' }, // Contact
            ],
          },
          {
            FormFieldName: 'campaign_id',
            DataverseFieldName: 'fus_campaignid',
          },
        ];

        const serializedForm = window.d365mktformcapture.serializeForm(
          form,
          mappings
        );

        const payload = serializedForm.SerializedForm.build();

        const captureConfig = {
          FormId: '6ca8b963-0eec-f011-8544-7ced8d347e2a',
          FormApiUrl:
            'https://public-oce.mkt.dynamics.com/api/v1.0/orgs/f1fe3a40-053b-47c4-b7d9-981e51994839/landingpageforms',
        };

        // Submit to D365 first, then submit the native form
        return window.d365mktformcapture.submitForm(captureConfig, payload);
      })
      .then(() => {
        console.log('D365 form capture complete, submitting native form');
        $form.submit();
      })
      .catch((error) => {
        console.error('Form capture error:', error);
        // Still submit the native form even if D365 fails
        $form.submit();
      });
  },

  isFormComplete() {
    const hasEmptyFields = Object.values(this.emptyFields).some(
      (value) => value
    );
    return !hasEmptyFields;
  },
}));

// Case study sliders
Alpine.data('article', () => ({
  hideArrows: {},
  disablePrev: {},
  disableNext: {},

  init() {
    const $sliders = document.querySelectorAll('.slider');

    $sliders.forEach(($slider) => {
      const { idx } = $slider.dataset;

      this.hideArrows[idx] = $slider.clientWidth === $slider.scrollWidth;
      this.disablePrev[idx] = true;

      $slider.addEventListener(
        'scroll',
        throttle((e) => {
          this.disablePrev[idx] = e.target.scrollLeft < 1;
          this.disableNext[idx] =
            e.target.scrollLeft + e.target.clientWidth >=
            e.target.scrollWidth - 20;
        }, 75)
      );

      window.addEventListener(
        'resize',
        throttle(() => {
          this.hideArrows[idx] = $slider.clientWidth === $slider.scrollWidth;
        }, 200)
      );
    });
  },

  slide(idx = 1, dirRight = true, gap = 20) {
    const $slider = document.querySelector(`.slider[data-idx="${idx}"]`);
    const width = $slider.lastElementChild.clientWidth + gap;
    $slider.scrollBy(dirRight ? width : width * -1, 0);
  },
}));

globalThis.Alpine = Alpine;

Alpine.start();

document.addEventListener('click', (event) => {
  if (event.target.matches('a') && event.target.hash === '#contact') {
    event.preventDefault();
    document.dispatchEvent(new CustomEvent('open-contact'));
  }
});
